<template>
    <div>
        <div class="row">
            <div class="col-md-6 d-md-flex align-items-baseline">
                <h1 class="mr-2">List of Appointment Types</h1>
            </div>
            <!-- SEARCH FUNCTION HERE -->
            <div class="col-md-6 pb-3">
                <input-search-icon
                    v-model="filter.search"
                    @search="searchAppointmentTypes"
                    :placeholder="'Search Appointment Type'"
                    haveItemOnRightSide
                >
                    <template v-slot:before>
                        <button
                            type="button"
                            class="btn btn-secondary mr-1"
                            @click="sortByArrangement"
                        >
                            Sort By Order
                            <span
                                v-if="isSortByArrangement"
                                class="fas fa-check pl-1"
                            ></span>
                        </button>
                    </template>
                    <template v-slot:after>
                        <button
                            v-if="_hasAccess('setting.appointment.type.create')"
                            type="button"
                            class="btn btn-secondary ml-1"
                            v-b-tooltip.hover.top
                            title="New Appointment Type"
                            @click="$refs.modalNewEdit.open()"
                        >
                            <span class="fas fa-plus"></span>
                        </button>
                    </template>
                </input-search-icon>
            </div>
        </div>
        <div class="card rounded-lg p-3">
            <div class="card-body overflow-auto p-0">
                <div class="table-holder table-responsive">
                    <LoaderList v-if="isLoading" class="w-100" />
                    <table v-else class="table list">
                        <thead>
                            <tr>
                                <th>
                                    <sorting
                                        :order-by="
                                            filter && filter.sortName
                                                ? filter.sortName
                                                : null
                                        "
                                        @sorting="
                                            orderBy =>
                                                sortingData(orderBy, 'name')
                                        "
                                        text="Appointment Type:"
                                    >
                                    </sorting>
                                </th>
                                <th>
                                    <sorting
                                        :order-by="
                                            filter && filter.sortDuration
                                                ? filter.sortDuration
                                                : null
                                        "
                                        @sorting="
                                            orderBy =>
                                                sortingData(orderBy, 'duration')
                                        "
                                        text="Duration:"
                                    >
                                    </sorting>
                                </th>
                                <th>Color:</th>
                                <th
                                    class="span1"
                                    v-if="extensions.online_booking"
                                >
                                    Book Online:
                                </th>
                                <th v-if="extensions.previsit">
                                    Pre-visit Confirmation:
                                </th>
                                <th
                                    width="8%"
                                    v-if="
                                        extensions.online_booking &&
                                        extensions.payment_addon
                                    "
                                >
                                    <sorting
                                        :order-by="
                                            filter && filter.sortDeposit
                                                ? filter.sortDeposit
                                                : null
                                        "
                                        @sorting="
                                            orderBy =>
                                                sortingData(orderBy, 'deposit')
                                        "
                                        text="Deposit:"
                                    >
                                    </sorting>
                                </th>
                                <th>Default:</th>
                                <th
                                    v-if="
                                        _hasAccess(
                                            'setting.appointment.type.update'
                                        ) ||
                                        _hasAccess(
                                            'setting.appointment.type.delete'
                                        )
                                    "
                                ></th>
                            </tr>
                        </thead>
                        <draggable
                            v-if="appointmentTypes"
                            :list="appointmentTypes"
                            @end="updateIndex"
                            tag="tbody"
                            handle=".handle"
                            :disabled="!isSortByArrangement"
                        >
                            <tr
                                v-for="appointmentType in appointmentTypes"
                                v-bind:key="appointmentType.id"
                                :id="appointmentType.id"
                            >
                                <td>
                                    <span
                                        class="cus-black-white-swatch handle"
                                        :style="{
                                            cursor: isSortByArrangement
                                                ? 'move'
                                                : 'default',
                                            opacity: isSortByArrangement
                                                ? 1
                                                : 0.5
                                        }"
                                        v-b-tooltip.hover.top
                                        :title="
                                            isSortByArrangement
                                                ? ''
                                                : `Click Sort By Order to enable this function`
                                        "
                                    ></span
                                    >{{ appointmentType.type }}
                                </td>
                                <td>{{ appointmentType.duration }} minutes</td>
                                <td>
                                    <input
                                        type="color"
                                        id="colorPicker"
                                        class="btn btn-secondary btn-color"
                                        :value="
                                            appointmentTypeColor(
                                                appointmentType
                                            )
                                        "
                                        @input="
                                            event =>
                                                updateColor(
                                                    appointmentType,
                                                    event.target.value
                                                )
                                        "
                                        :disabled="
                                            isColorBtnDisable ||
                                            !_hasAccess(
                                                'setting.appointment.type.update'
                                            )
                                        "
                                    />
                                    <spinner
                                        v-if="appointmentType.isLoading"
                                    ></spinner>
                                    <span
                                        v-else-if="
                                            appointmentType.new_color_code
                                        "
                                    >
                                        <i
                                            class="fas fa-check px-2"
                                            v-b-tooltip.hover.top
                                            title="Save color change"
                                            @click="saveColor(appointmentType)"
                                        >
                                        </i>
                                        <i
                                            class="fas fa-times"
                                            v-b-tooltip.hover.top
                                            title="Cancel"
                                            @click="
                                                cancelColor(appointmentType)
                                            "
                                        >
                                        </i>
                                    </span>
                                </td>

                                <td v-if="extensions.online_booking">
                                    {{ appointmentType.online ? "Yes" : "No" }}
                                </td>
                                <td v-if="extensions.previsit">
                                    {{
                                        appointmentType.previsit ? "Yes" : "No"
                                    }}
                                </td>
                                <td
                                    v-if="
                                        extensions.online_booking &&
                                        extensions.payment_addon
                                    "
                                >
                                    {{
                                        _toCurrency(
                                            appointmentType.deposit || 0
                                        )
                                    }}
                                </td>
                                <td>
                                    <div v-if="appointmentType.soap_template">
                                        Medical Note:
                                        <TemplateLink
                                            v-if="
                                                appointmentType.soap_template &&
                                                appointmentType.soap_template
                                                    .encoded_id
                                            "
                                            :temp="
                                                getSoapTemplateURI(
                                                    appointmentType
                                                )
                                            "
                                        />
                                        <span
                                            v-else
                                            v-html="
                                                getSoapTemplateURI(
                                                    appointmentType
                                                )
                                            "
                                        >
                                        </span>
                                    </div>
                                    <div v-if="appointmentType.bundle_template">
                                        Bundle:
                                        <TemplateLink
                                            v-if="
                                                appointmentType.bundle_template &&
                                                appointmentType.bundle_template
                                                    .encoded_id
                                            "
                                            :temp="
                                                getBundleTemplateURI(
                                                    appointmentType
                                                )
                                            "
                                        />
                                        <span
                                            v-else
                                            v-html="
                                                getBundleTemplateURI(
                                                    appointmentType
                                                )
                                            "
                                        ></span>
                                    </div>
                                    <div>
                                        Checkout Documents:
                                        <span>{{
                                            getCheckoutDocuments(
                                                appointmentType
                                            ).text
                                        }}</span>
                                        <span
                                            v-if="
                                                getCheckoutDocuments(
                                                    appointmentType
                                                ).remainingCount
                                            "
                                            class="text-primary font-weight-bold"
                                            style="cursor: pointer;"
                                            @click="
                                                expandDocument(appointmentType)
                                            "
                                        >
                                            ... ({{
                                                getCheckoutDocuments(
                                                    appointmentType
                                                ).remainingCount
                                            }}
                                            more)
                                        </span>
                                    </div>
                                    <div>
                                        Confirmation:
                                        <TemplateLink
                                            v-if="
                                                appointmentType.confirmation_template &&
                                                appointmentType
                                                    .confirmation_template
                                                    .encoded_id
                                            "
                                            :temp="
                                                getConfirmationTemplateURI(
                                                    appointmentType
                                                )
                                            "
                                        />
                                        <span
                                            v-else
                                            v-html="
                                                getConfirmationTemplateURI(
                                                    appointmentType
                                                )
                                            "
                                        ></span>
                                    </div>
                                    <div>
                                        Reminder:
                                        <TemplateLink
                                            v-if="
                                                appointmentType.reminder_template &&
                                                appointmentType
                                                    .reminder_template
                                                    .encoded_id
                                            "
                                            :temp="
                                                getReminderTemplateURI(
                                                    appointmentType
                                                )
                                            "
                                        />
                                        <span
                                            v-else
                                            v-html="
                                                getReminderTemplateURI(
                                                    appointmentType
                                                )
                                            "
                                        ></span>
                                    </div>
                                    <div
                                        v-if="
                                            appointmentType.checkin_webforms_templates &&
                                            appointmentType
                                                .checkin_webforms_templates
                                                .length > 0
                                        "
                                    >
                                        Check-In Forms:
                                        <span
                                            v-for="(webform,
                                            index) in appointmentType.checkin_webforms_templates"
                                            :key="webform.id"
                                        >
                                            <WebformLink
                                                router-name="template_details"
                                                :useRouter="false"
                                                :webform="webform.template"
                                                :withComma="
                                                    appointmentType
                                                        .checkin_webforms_templates
                                                        .length -
                                                        1 !=
                                                    index
                                                "
                                            />
                                        </span>
                                    </div>
                                    <div
                                        v-if="
                                            extensions.previsit &&
                                            appointmentType.previsit == 1
                                        "
                                    >
                                        Pre-visit:
                                        <TemplateLink
                                            v-if="
                                                appointmentType.mobile_template_id &&
                                                !appointmentType.custom_sms_previsit
                                            "
                                            :temp="
                                                getLetterTemplate(
                                                    appointmentType.mobile_template_id
                                                )
                                            "
                                        />
                                        <span v-else>
                                            {{
                                                appointmentType.custom_sms_previsit
                                                    ? "Custom SMS"
                                                    : "System Default"
                                            }}
                                        </span>
                                    </div>
                                    <div
                                        v-if="
                                            extensions.previsit &&
                                            appointmentType.previsit == 1 &&
                                            appointmentType.documents.length
                                        "
                                    >
                                        Pre-visit Letters:
                                        <span
                                            v-for="(document,
                                            index) in appointmentType.documents"
                                            :key="document.id"
                                        >
                                            <TemplateLink
                                                :temp="
                                                    getLetterTemplate(
                                                        document.letter_template_id
                                                    )
                                                "
                                            />
                                            <span
                                                v-if="
                                                    appointmentType.documents
                                                        .length -
                                                        1 !=
                                                    index
                                                "
                                                >,
                                            </span>
                                        </span>
                                    </div>
                                    <div
                                        v-if="
                                            extensions.previsit &&
                                            appointmentType.previsit == 1 &&
                                            appointmentType
                                                .previsit_webforms_templates
                                                .length
                                        "
                                    >
                                        Pre-visit Forms:
                                        <span
                                            v-for="(webform,
                                            index) in appointmentType.previsit_webforms_templates"
                                            :key="webform.id"
                                        >
                                            <WebformLink
                                                router-name="template_details"
                                                :useRouter="false"
                                                :webform="webform.template"
                                                :withComma="
                                                    appointmentType
                                                        .previsit_webforms_templates
                                                        .length -
                                                        1 !=
                                                    index
                                                "
                                            />
                                        </span>
                                    </div>
                                </td>
                                <td
                                    class="text-right small text-nowrap"
                                    v-if="
                                        _hasAccess(
                                            'setting.appointment.type.update'
                                        ) ||
                                        _hasAccess(
                                            'setting.appointment.type.delete'
                                        )
                                    "
                                >
                                    <div>
                                        <!-- <?php if(check_access(TblPermissionPeer::SETTING_ROOM_UPDATE)) { ?> -->
                                        <span
                                            class="cursor-pointer"
                                            v-if="
                                                _hasAccess(
                                                    'setting.appointment.type.update'
                                                )
                                            "
                                            @click="
                                                !isColorBtnDisable &&
                                                    edit(appointmentType.id)
                                            "
                                        >
                                            Edit
                                        </span>
                                        <span
                                            v-if="
                                                !appointmentType.isdefault &&
                                                _hasAccess(
                                                    'setting.appointment.type.update'
                                                ) &&
                                                _hasAccess(
                                                    'setting.appointment.type.delete'
                                                )
                                            "
                                            >|</span
                                        >
                                        <!-- <?php if(check_access(TblPermissionPeer::SETTING_ROOM_DELETE) && !$appointment_type->getIsDefault()) { ?> -->
                                        <span
                                            v-if="
                                                !appointmentType.isdefault &&
                                                _hasAccess(
                                                    'setting.appointment.type.delete'
                                                )
                                            "
                                            class="cursor-pointer"
                                            @click="
                                                !isColorBtnDisable &&
                                                    remove(appointmentType)
                                            "
                                        >
                                            Delete
                                        </span>
                                    </div>
                                </td>
                            </tr>
                        </draggable>
                        <tbody v-if="appointmentTypes.length == 0">
                            <tr>
                                <td :colspan="9">
                                    <i>No records available</i>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            <pagination
                :current-page="filter.page"
                :totalPages="totalPages"
                :isLoading="isLoading"
                @change-page="page => changePage(page)"
            />
        </div>

        <modal-new-edit ref="modalNewEdit" @success="getAppointmentTypes" />
        <modal-delete ref="modalDelete" @reload="getAppointmentTypes()" />
    </div>
</template>

<script>
/**
 * Reference : ..apps/application/views/staff/business/sub/appointment_type/list.php
 */
import appointmentTypesService from "@/services/setting/appointment-type";
import WebformLink from "@/components/commons/webform-link";
import { find } from "lodash";

export default {
    components: {
        LoaderList: () => import("@/components/elements/loader-list.vue"),
        Sorting: () => import("@/module/commons/sorting.vue"),
        Pagination: () => import("@/components/commons/pagination.vue"),
        InputSearchIcon: () => import("@/module/commons/input-search-icon.vue"),
        Spinner: () => import("@/components/elements/spinner.vue"),
        Draggable: () => import("vuedraggable"),
        TemplateLink: () => import("@/components/commons/template-link"),
        ModalNewEdit: () => import("./modal-new-edit.vue"),
        ModalDelete: () => import("./modal-delete.vue"),
        WebformLink
    },
    data() {
        return {
            appointmentTypes: [],
            isLoading: true,
            filter: {
                search: "",
                sortName: "",
                sortDuration: "",
                sortDeposit: "",
                perPage: 20,
                page: 1
            },
            totalPages: 0,
            documentOptions: {
                1: "Rabies Certificate",
                2: "Vaccine Certificate",
                3: "Reminders Summary",
                4: "Invoice",
                6: "Letter",
                7: "Medical Note",
                9: "Treatment Summary",
                10: "Form"
            },
            permissions: [],
            extensions: [],
            my_time_zone: "",
            my_date_format_for_moment: "",
            currency_symbol: "",
            letters: [],
            isSortByArrangement: true,
            isColorBtnDisable: false
        };
    },
    computed: {
        refresh() {
            return this.$store.getters["appointment-types/refresh"];
        }
    },
    async created() {
        this.getAppointmentTypes();
        this.getLetter();
        this.getExtension();
        this.getCurrencySymbol();
    },
    watch: {
        filter: {
            handler() {
                this.getAppointmentTypes();
            },
            deep: true
        },
        refresh() {
            this.getAppointmentTypes();
        }
    },
    methods: {
        edit(id) {
            this.$refs.modalNewEdit.open({
                id: id,
                isEdit: true
            });
        },
        remove(data = {}) {
            this.$refs.modalDelete.open({
                ...data
            });
        },
        async getAppointmentTypes() {
            this.isLoading = true;
            const res = await appointmentTypesService.getAppointmentTypes(
                this.filter
            );
            const { meta, data } = res.data.response.resource;
            if (res.status == 200) {
                this.appointmentTypes = data;
                this.totalPages = meta.lastPage;
            }
            this.isLoading = false;
        },
        async getLetter() {
            await appointmentTypesService.getLetter().then(response => {
                this.letters = response.data.response.resources;
            });
        },
        async getExtension() {
            let self = this;
            await appointmentTypesService.getExtension().then(response => {
                self.extensions = {
                    online_booking: !!find(response.data.response.resources, {
                        extension_code: "ONSCHEDULE"
                    }),
                    previsit: !!find(response.data.response.resources, {
                        extension_code: "PREVISIT"
                    }),
                    wellness: !!find(response.data.response.resources, {
                        extension_code: "WELLNESS"
                    }),
                    payment_addon:
                        find(response.data.response.resources, {
                            extension_code: "CCONNECT"
                        }) ||
                        find(response.data.response.resources, {
                            extension_code: "GRAVITY"
                        }) ||
                        find(response.data.response.resources, {
                            extension_code: "STRIPE"
                        })
                };
            });
        },
        sortingData(orderBy, col) {
            this.filter.page = 1;
            this.isSortByArrangement = false;
            if (col == "name") {
                this.filter.sortName = orderBy;
                this.filter.sortDuration = "";
                this.filter.sortDeposit = "";
            } else if (col == "duration") {
                this.filter.sortName = "";
                this.filter.sortDuration = orderBy;
                this.filter.sortDeposit = "";
            } else if (col == "deposit") {
                this.filter.sortName = "";
                this.filter.sortDuration = "";
                this.filter.sortDeposit = orderBy;
            }
        },
        changePage(page) {
            this.filter.page = page;
        },
        searchAppointmentTypes(keyword) {
            this.filter.search = keyword;
            this.resetFilter();
        },
        resetFilter() {
            this.filter.sortDuration = "";
            this.filter.sortName = "";
            this.filter.sortDeposit = "";
            this.filter.page = 1;
        },
        async updateIndex(obj) {
            var params = [];
            $.each(this.appointmentTypes, function (index, object) {
                params.push({
                    index: index,
                    id: object.id,
                    value: "reorder"
                });
            });
            if (params.length > 0) {
                await appointmentTypesService.reorderAppointmentType(params);
            }
        },
        async getCurrencySymbol() {
            this.currency_symbol = this.$store.state.setting.currency_symbol;
        },
        getLetterTemplate(id) {
            return find(this.letters, { id });
        },
        getSoapTemplateURI(appointmentType) {
            if (appointmentType.soap_template) {
                if (appointmentType.soap_template.encoded_id) {
                    return appointmentType.soap_template;
                } else {
                    return (
                        '<a href="javascript:void(0);">' +
                        appointmentType.soap_template.name +
                        "</a>"
                    );
                }
            }

            return "-";
        },
        getBundleTemplateURI(appointmentType) {
            if (appointmentType.bundle_template) {
                if (appointmentType.bundle_template.encoded_id) {
                    return appointmentType.bundle_template;
                } else {
                    return (
                        '<a href="javascript:void(0);">' +
                        appointmentType.bundle_template.name +
                        "</a>"
                    );
                }
            }

            return "-";
        },
        getCheckoutDocuments(appointmentType) {
            let defaultDocuments = ["Invoice"];
            let self = this;

            if (self._self.extensions.wellness) {
                self.documentOptions[8] = "Wellness Summary";
                defaultDocuments = ["Invoice", "Wellness Summary"];
            }

            if (appointmentType.checkout_document_type_ids) {
                defaultDocuments = [];
                let checkoutDocumentTypeIds = appointmentType.checkout_document_type_ids.split(
                    ","
                );

                $.each(checkoutDocumentTypeIds, function (index, object) {
                    if (self.documentOptions[parseInt(object)])
                        defaultDocuments.push(
                            self.documentOptions[parseInt(object)]
                        );
                });
            }

            if (defaultDocuments.length > 2 && !appointmentType?.expanded) {
                const shownItems = defaultDocuments.slice(0, 2).join(", ");
                const remainingCount = defaultDocuments.length - 2;

                return {
                    text: `${shownItems}`,
                    remainingCount,
                    appointmentType
                };
            }

            return {
                text: defaultDocuments.join(", ")
            };
        },
        getConfirmationTemplateURI(appointmentType) {
            if (appointmentType.custom_sms_confirmation) {
                return "Custom SMS";
            } else {
                if (appointmentType.confirmation_template_id) {
                    if (appointmentType.confirmation_template) {
                        if (appointmentType.confirmation_template.encoded_id) {
                            return appointmentType.confirmation_template;
                        } else {
                            return (
                                '<a href="javascript:void(0);">' +
                                appointmentType.confirmation_template.name +
                                "</a>"
                            );
                        }
                    } else {
                        return "No Confirmation";
                    }
                } else {
                    return "Email Default";
                }
            }
        },
        getReminderTemplateURI(appointmentType) {
            if (appointmentType.custom_sms_reminder) {
                return "Custom SMS";
            } else {
                if (appointmentType.reminder_template_id) {
                    if (appointmentType.reminder_template) {
                        if (appointmentType.reminder_template.encoded_id) {
                            return appointmentType.reminder_template;
                        } else {
                            return (
                                '<a href="javascript:void(0);">' +
                                appointmentType.reminder_template.name +
                                "</a>"
                            );
                        }
                    } else {
                        return "No Reminder";
                    }
                } else {
                    return "Email Default";
                }
            }
        },
        sortByArrangement() {
            if (this.isSortByArrangement) {
                this.isSortByArrangement = false;
                this.sortingData("asc", "name");
            } else {
                this.appointmentTypes.sort((a, b) => a.index - b.index);
                this.resetFilter();
                this.isSortByArrangement = true;
            }
        },
        expandDocument(appointmentType) {
            const index = this.appointmentTypes.findIndex(
                aT => aT.id === appointmentType.id
            );
            if (index !== -1) {
                this.$set(this.appointmentTypes[index], "expanded", true);
            }
        },
        updateColor(data = {}, color) {
            const index = this.appointmentTypes.findIndex(
                aT => aT.id === data.id
            );
            if (index !== -1) {
                this.$set(this.appointmentTypes, index, {
                    ...this.appointmentTypes[index],
                    new_color_code: color
                });
            }
            console.log(this.appointmentTypes);
        },
        appointmentTypeColor(appointmentType) {
            return (
                appointmentType?.new_color_code ??
                `#${appointmentType.color_code}`
            );
        },
        async saveColor(data) {
            const index = this.appointmentTypes.findIndex(
                aT => aT.id === data.id
            );
            if (index !== -1) {
                this.$set(this.appointmentTypes, index, {
                    ...this.appointmentTypes[index],
                    isLoading: true
                });
                this.isColorBtnDisable = true;

                appointmentTypesService
                    .updateAppointmentType({
                        id: data.id,
                        type: data.type,
                        duration: data.duration || 0,
                        color_code: data.new_color_code
                    })
                    .then(response => {
                        if (response.message && response.message.code == 200) {
                            this.getAppointmentTypes();

                            this._message(
                                "Appointment type's color has been successfully updated."
                            );
                        } else {
                            this._message(response.message, "danger");
                        }
                    })
                    .catch(error => {
                        this._message(
                            "Error : Unable to update appointment type's color",
                            "danger"
                        );
                        console.error(error);
                    })
                    .finally(() => {
                        this.isColorBtnDisable = false;
                        this.$set(this.appointmentTypes, index, {
                            ...this.appointmentTypes[index],
                            isLoading: false
                        });
                    });
            }
        },
        async cancelColor(data) {
            const index = this.appointmentTypes.findIndex(
                aT => aT.id === data.id
            );
            if (index !== -1) {
                this.$set(
                    this.appointmentTypes,
                    index,
                    (({ new_color_code, ...rest }) => rest)(
                        this.appointmentTypes[index]
                    )
                );
            }
        }
    }
};
</script>
<style scoped>
/* Button styles */
.btn-color {
    -webkit-appearance: none; /* Remove default styles in WebKit browsers */
    -moz-appearance: none; /* Remove default styles in Mozilla browsers */
    appearance: none; /* Remove default styles in modern browsers */
    width: 40px; /* Set button width */
    height: 30px; /* Set button height */
    border-radius: 6px; /* Apply rounded corners */
    border: 1px solid #ccc; /* Set border */
    cursor: pointer; /* Change cursor to pointer */
    outline: none; /* Remove focus outline */
    padding: 2px; /* Remove padding */
    background-color: #ffffff; /* Set background color */
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1); /* Add subtle shadow */
    overflow: hidden; /* Prevent content overflow */
}

/* Remove the inner square (default color preview) */
.btn-color::-webkit-color-swatch {
    border: none;
    border-radius: 4px;
    padding: 0;
    margin: 0;
}

.btn-color::-moz-color-swatch {
    border: none;
    border-radius: 4px;
}
</style>
